import { CalendarPlus, Bell } from "lucide-react";

/**
 * Recordatorio semanal: nuevos episodios los jueves 11:00 AM (ET).
 * Permite agendar un evento recurrente (.ics) en cualquier calendario.
 */
const buildIcs = () => {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//NBT Podcast//Nuevos episodios//ES",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VTIMEZONE",
    "TZID:America/New_York",
    "BEGIN:DAYLIGHT",
    "TZOFFSETFROM:-0500",
    "TZOFFSETTO:-0400",
    "TZNAME:EDT",
    "DTSTART:19700308T020000",
    "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU",
    "END:DAYLIGHT",
    "BEGIN:STANDARD",
    "TZOFFSETFROM:-0400",
    "TZOFFSETTO:-0500",
    "TZNAME:EST",
    "DTSTART:19701101T020000",
    "RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU",
    "END:STANDARD",
    "END:VTIMEZONE",
    "BEGIN:VEVENT",
    "UID:nuevo-episodio-semanal@nbtpodcast.com",
    "DTSTAMP:20260101T000000Z",
    "DTSTART;TZID=America/New_York:20260101T110000",
    "DTEND;TZID=America/New_York:20260101T120000",
    "RRULE:FREQ=WEEKLY;BYDAY=TH",
    "SUMMARY:Nuevo episodio de NBT Podcast",
    "DESCRIPTION:Cada jueves a las 11:00 AM (ET) publicamos un nuevo episodio de NBT Podcast con Ricardo Quintero.",
    "URL:https://nbtpodcast.com",
    "LOCATION:https://www.youtube.com/@NBTPodcast",
    "BEGIN:VALARM",
    "TRIGGER:-PT15M",
    "ACTION:DISPLAY",
    "DESCRIPTION:Nuevo episodio de NBT Podcast en 15 minutos",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return lines.join("\r\n");
};

const GOOGLE_CALENDAR_URL =
  "https://calendar.google.com/calendar/render?action=TEMPLATE" +
  "&text=" + encodeURIComponent("Nuevo episodio de NBT Podcast") +
  // Floating local times + ctz keep the event at 11:00 AM ET year-round
  // (a UTC-anchored recurrence would drift one hour after DST ends).
  "&dates=20260101T110000/20260101T120000" +
  "&ctz=" + encodeURIComponent("America/New_York") +
  "&recur=" + encodeURIComponent("RRULE:FREQ=WEEKLY;BYDAY=TH") +
  "&details=" +
  encodeURIComponent(
    "Cada jueves a las 11:00 AM (ET) publicamos un nuevo episodio de NBT Podcast con Ricardo Quintero.\nhttps://nbtpodcast.com"
  ) +
  "&location=" + encodeURIComponent("https://www.youtube.com/@NBTPodcast");

const days = [
  {
    day: "Jueves",
    title: "NBT Podcast",
    body: "Conversaciones sin filtros con las personas que están definiendo la cultura, los negocios y el futuro de Latinoamérica.",
    live: true,
    meta: "11:00 AM ET | Nuevo episodio cada semana.",
  },
  {
    day: "Siempre",
    title: "Archivo completo",
    body: "Más de 100 episodios on demand en YouTube, Spotify y Apple Podcasts.",
  },
  {
    day: "Comunidad",
    title: "Alertas y newsletter",
    body: "Recibe el nuevo episodio y las historias de la semana directo en tu correo.",
  },
];

const WeeklyScheduleBar = () => {
  const handleAddToCalendar = () => {
    const blob = new Blob([buildIcs()], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "nbt-podcast-nuevos-episodios.ics";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleNewsletterScroll = () => {
    // The newsletter section is lazy-mounted: scroll down to trigger it, then poll for it.
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    let tries = 0;
    const timer = window.setInterval(() => {
      const el = document.getElementById("newsletter");
      if (el) {
        window.clearInterval(timer);
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (++tries > 60) {
        window.clearInterval(timer);
      }
    }, 250);
  };

  return (
    <section aria-label="Horario semanal de episodios" className="bg-background pt-14 md:pt-16 pb-4">
      <div className="site-wrap">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-6">
          <div>
            <p className="eyebrow">Agenda semanal</p>
            <h2 className="mt-1 font-serif text-3xl md:text-[34px] font-semibold text-foreground">
              Nuevos episodios cada semana
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href={GOOGLE_CALENDAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-subscribe"
              aria-label="Agregar recordatorio semanal a Google Calendar"
            >
              <span className="text-base leading-none">+</span>
              Google Calendar
            </a>
            <button
              type="button"
              onClick={handleAddToCalendar}
              className="btn-outline-pill"
              aria-label="Descargar recordatorio para Apple Calendar u otros calendarios"
            >
              Apple / Otros
            </button>
            <button type="button" onClick={handleNewsletterScroll} className="btn-outline-pill">
              Recibir alertas por email
            </button>
          </div>
        </div>

        <div className="flex flex-col divide-y divide-border border border-border rounded-xl overflow-hidden bg-card md:grid md:grid-cols-3 md:divide-y-0">
          {days.map((item, i) => (
            <article
              key={item.day}
              className={`p-6 ${i > 0 ? "md:border-l border-border" : ""}`}
            >
              <p className="text-[11px] font-sans font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2">
                {item.day}
              </p>
              <h3 className="font-serif text-lg font-semibold text-foreground leading-snug">
                {item.title}
              </h3>
              {item.live && (
                <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  Live
                </span>
              )}
              {item.meta && (
                <p className="mt-2 text-sm text-foreground">{item.meta}</p>
              )}
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WeeklyScheduleBar;
